import {
  Autocomplete,
  AutocompleteRenderOptionState,
  Checkbox,
  Chip,
  CircularProgress,
  ListItemText,
  MenuItem,
  TextField,
} from '@mui/material';
import { ReactNode, Ref, useMemo } from 'react';
import { BaseLocationDataModel } from '../../schemas/Location.schema';
import { getLocations } from '../../services/queries/LocationQueries';
import { IOptionsFromSearchConfig, useAsyncAutoComplete } from '../Form/Display/useAsyncAutomplete';
import { MoreChipWithTooltip } from '../../pages/CompanyProfiles/Summary/components/MoreChipWithTooltip/MoreChipWithTooltip';
import { FMT } from '@foresightdata/web-common-formatter-service';

export interface ILocationSearchProps {
  className?: string;
  disabled: boolean;
  initialValue?: BaseLocationDataModel | undefined;
  multiple?: boolean;
  onChange: (location: BaseLocationDataModel | BaseLocationDataModel[] | null) => void;
  /** Rendered before the value (and before the tag chips when `multiple`). */
  startAdornment?: ReactNode;
  /** See `ISelectMeta.inlinePopper`. Off by default, so the dropdown stays portaled to `body`. */
  inlinePopper?: boolean;
}

// React requires the "value" to be defined for this to be a controlled component,
// undefined will trigger an error and result in unwanted behaviour, hence this dummy.
// See https://github.com/facebook/react/issues/6222.
const dummyLocation: BaseLocationDataModel = { city: '', country: '', state: '' };

export function LocationSearch({ ref, ...props }: ILocationSearchProps & { ref?: Ref<HTMLInputElement> }) {
  const {
    className,
    disabled = false,
    initialValue,
    onChange,
    multiple = false,
    startAdornment,
    inlinePopper,
  } = props;

  const autoCompleteConfig = useMemo(() => {
    const searchFn = (queryText: string) => getLocations(queryText);
    const conf: IOptionsFromSearchConfig<BaseLocationDataModel, typeof multiple> = {
      initialValue: initialValue ?? (multiple ? [] : dummyLocation),
      searchFn,
      onChange,
    };
    return conf;
  }, [initialValue, multiple, onChange]);

  const { isSearching, onClose, onInputChange, onFocus, onValueChange, open, options, value } =
    useAsyncAutoComplete(autoCompleteConfig);

  return (
    <Autocomplete
      autoHighlight
      blurOnSelect
      className={className}
      disablePortal={true}
      disabled={disabled}
      disableClearable={true}
      disableCloseOnSelect={multiple}
      filterOptions={(options) => options}
      fullWidth={true}
      getOptionLabel={formatLocation}
      id='locations-autocomplete'
      isOptionEqualToValue={(option, newValue) => {
        return locationsEqual(option, newValue);
      }}
      multiple={multiple}
      noOptionsText={null}
      onChange={onValueChange}
      onClose={onClose}
      onFocus={onFocus}
      onInputChange={onInputChange}
      open={open}
      options={options}
      popupIcon={null}
      // Opt-in: the popper takes the anchor's width, which clips long locations under a narrow
      // input (the Digest rail). Left alone elsewhere, where the floor could overflow the row.
      slotProps={
        inlinePopper ? { popper: { style: { minWidth: '15rem' }, placement: 'bottom-start' } } : undefined
      }
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            inputRef={ref}
            InputProps={{
              ...params.InputProps,
              // Composed before the params' own adornment, which carries the tag chips when
              // `multiple` — replacing it outright would drop them.
              startAdornment: startAdornment ? (
                <>
                  {startAdornment}
                  {params.InputProps.startAdornment}
                </>
              ) : (
                params.InputProps.startAdornment
              ),
              endAdornment: (
                <>
                  {isSearching ? <CircularProgress color='secondary' size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        );
      }}
      renderOption={(props, option, renderOptionState: AutocompleteRenderOptionState) => {
        const { selected } = renderOptionState;
        const locationStr = formatLocation(option);

        return (
          <MenuItem {...props} key={locationStr} disabled={disabled}>
            {multiple ? <Checkbox checked={selected} /> : null}
            <ListItemText primary={locationStr} />
          </MenuItem>
        );
      }}
      renderTags={multiple ? tagRenderer : undefined}
      value={value ?? undefined}
      ListboxProps={{
        style: { maxHeight: 190 },
      }}
    />
  );
}

function formatLocation(loc?: BaseLocationDataModel | null) {
  return FMT.format('location', loc);
}

function locationsEqual(location1: BaseLocationDataModel, location2: BaseLocationDataModel) {
  return formatLocation(location1) === formatLocation(location2);
}

function tagRenderer(options: BaseLocationDataModel[]) {
  if (!options?.length) {
    return <></>;
  }

  const [first, ...others] = options.map((option) => {
    return formatLocation(option);
  });

  if (options.length === 1) {
    return <Chip size='small' label={first} />;
  }
  return (
    <>
      <Chip size='small' label={first} />
      <MoreChipWithTooltip
        title={`Other (${others.length})`}
        moreChipTotal={others.length}
        otherItems={others}
      ></MoreChipWithTooltip>
    </>
  );
}
